import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useResendTimer } from "../../hooks/useResendTimer";
import { verifyEmailWithCode, resendVerificationEmail } from "../../api/authApi";

/**
 * Six individual digit cells for OTP entry.
 * Handles auto-advance, backspace, paste, and fires onComplete when all 6 are filled.
 *
 * @param {boolean}  hasError    - When true, cells render in the error colour.
 * @param {function} onComplete  - Called with the 6-digit string when all cells are filled.
 * @param {function} onChange    - Called on every keystroke with the current partial string.
 */
const SegmentedCodeInput = ({ hasError, onComplete, onChange }) => {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];

  const cellClass = (error) =>
    `w-11 h-12 rounded-card border text-center font-mono text-lg shadow-hairline focus:outline-none focus:ring-2 focus:ring-accent-500 ${
      error
        ? "border-signal-red-500 text-signal-red-600"
        : "border-ink-200 text-ink-900"
    }`;

  const update = (index, value, currentDigits) => {
    const next = [...currentDigits];
    next[index] = value;
    setDigits(next);
    const joined = next.join("");
    onChange(joined);
    return { next, joined };
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index] !== "") {
        // Clear current cell
        update(index, "", digits);
      } else if (index > 0) {
        // Step back and clear the previous cell
        const { next } = update(index - 1, "", digits);
        refs[index - 1].current?.focus();
      }
    }
  };

  const handleInput = (index, e) => {
    // Accept only the last typed digit (handles IME / paste-via-keyboard)
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) return;
    const digit = raw.slice(-1);
    const { next, joined } = update(index, digit, digits);

    if (index < 5) {
      refs[index + 1].current?.focus();
    }
    if (next.every((d) => d !== "")) {
      onComplete(joined);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);
    const joined = next.join("");
    onChange(joined);
    // Focus the cell after the last pasted digit, or the last cell
    const focusIndex = Math.min(pasted.length, 5);
    refs[focusIndex].current?.focus();
    if (next.every((d) => d !== "")) {
      onComplete(joined);
    }
  };

  return (
    <div
      role="group"
      aria-label="6-digit verification code"
      className="flex gap-2"
      onPaste={handlePaste}
    >
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit}
          aria-label={`Digit ${i + 1}`}
          className={cellClass(hasError)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onChange={(e) => handleInput(i, e)}
        />
      ))}
    </div>
  );
};

/**
 * Shared 6-digit email verification code entry component.
 * Uses a segmented 6-cell OTP input with auto-advance, backspace navigation,
 * paste-fill, and auto-submit on the 6th digit.
 * Used by: BookingPageForm (inline go-live gate), OnboardingDashboard (dashboard nudge),
 * and VerifiedSubmitButton (verification modal). Parents send the code (on demand)
 * before revealing this widget; this component only enters/verifies/resends.
 *
 * @param {function} onSuccess   - Called with the API response when the code verifies.
 * @param {string}   email       - Address the code was sent to. Required: it drives the
 *   resend request and the reassurance copy. Passed explicitly (not read from AuthContext)
 *   so this component works wherever it's mounted, including outside the AuthProvider.
 * @param {boolean}  [initialSent] - When true, the reassurance copy reads "New code sent
 *   to X" from the first render — set by parents that just sent a code before revealing.
 * @param {string}   [cooldownKey] - sessionStorage key for the resend cooldown. Each mount
 *   surface should pass its own key (and reuse it for the parent's initial send) so a cooldown
 *   on one surface does not disable the Resend button on an unrelated surface.
 * @param {string}   [reassurance] - Optional copy shown below the input.
 * @param {string}   [label]     - Optional field label. Defaults to the go-live phrasing.
 */
const VerifyCodeInput = ({
  onSuccess,
  email,
  initialSent = false,
  cooldownKey = "verifyCodeResendTimer",
  reassurance,
  label = "Enter the 6-digit code we emailed you to go live",
}) => {
  const queryClient = useQueryClient();
  const { canResend, timeLeft, startCooldown } = useResendTimer(120, cooldownKey);
  // Whether a fresh code was just sent (by a parent before reveal, or via Resend).
  // Drives the "New code sent to X" vs "Check X for your code" reassurance copy.
  const [hasResent, setHasResent] = useState(initialSent);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm();

  const { mutate: verify } = useMutation({
    mutationFn: (code) => verifyEmailWithCode(code),
    onSuccess: (data) => {
      // Refresh auth + setup state so callers see isEmailVerified === true immediately
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      queryClient.invalidateQueries({ queryKey: ["setupProgress"] });
      if (onSuccess) onSuccess(data);
    },
    onError: (err) => {
      const apiError = err.response?.data?.error;
      const message = apiError?.message || "Something went wrong. Please try again.";
      // Surface the server message inline (covers INVALID_CODE and the
      // TOO_MANY_CODE_ATTEMPTS lockout, which already tells the user to resend).
      setError("code", { type: "manual", message });
    },
  });

  const { mutate: resend, isPending: isResending } = useMutation({
    mutationFn: () => resendVerificationEmail({ email }),
    onSuccess: () => {
      setHasResent(true);
      startCooldown();
      clearErrors("code");
    },
    onError: () => {
      setError("code", {
        type: "manual",
        message: "Failed to resend code. Please try again.",
      });
    },
  });

  const onSubmit = useCallback(
    ({ code }) => {
      verify(code);
    },
    [verify]
  );

  const getResendLabel = () => {
    if (isResending) return "Sending…";
    if (!canResend) {
      const m = Math.floor(timeLeft / 60);
      const s = (timeLeft % 60).toString().padStart(2, "0");
      return `Resend in ${m}:${s}`;
    }
    return "Resend code";
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* Hidden RHF-registered field owns validation; cells call setValue to update it */}
      <input
        type="hidden"
        {...register("code", {
          required: "Enter the 6-digit code",
          pattern: { value: /^\d{6}$/, message: "Enter a 6-digit number" },
        })}
      />

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-ink-900 mb-1">
            {label}
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <SegmentedCodeInput
              hasError={!!errors.code}
              onChange={(joined) => {
                setValue("code", joined, { shouldValidate: false });
                if (errors.code) clearErrors("code");
              }}
              onComplete={(joined) => {
                setValue("code", joined, { shouldValidate: false });
                // Auto-submit when all 6 digits are entered
                handleSubmit(onSubmit)();
              }}
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center rounded-pill bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-400 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? "Verifying…" : "Verify"}
            </button>
          </div>

          {errors.code && (
            <p className="mt-1.5 text-sm text-signal-red-600" role="alert">
              {errors.code.message}
            </p>
          )}
        </div>

        {reassurance && (
          <p className="text-sm text-ink-500">{reassurance}</p>
        )}

        <p className="text-sm text-ink-500">
          {email && (
            hasResent ? (
              <>New code sent to <span className="font-medium text-ink-700">{email}</span>. </>
            ) : (
              <>Check <span className="font-medium text-ink-700">{email}</span> for your code. </>
            )
          )}
          <button
            type="button"
            onClick={() => resend()}
            disabled={!canResend || isResending}
            className="underline text-accent-600 hover:text-accent-500 disabled:text-ink-400 disabled:no-underline disabled:cursor-not-allowed"
          >
            {getResendLabel()}
          </button>
        </p>
      </div>
    </form>
  );
};

export default VerifyCodeInput;
